import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Fade, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { useAppBarHeight } from '~/components/global/appBarHeight';
import type { CfiRating } from '~/data/hooks/useCfi';
import { BUTTON_SX, SURFACE_RADIUS } from '~/theme';

import { CfiDownloadButton } from './CfiDownloadButton';
import { ratingColors, ratingLabels } from './ratingLabels';

type Counts = Record<CfiRating, number>;

type Props = {
  canEdit: boolean;
  counts: Counts;
  reportId: string;
  onPublish: () => Promise<void>;
  onAddFinding: () => void;
  /** True once the findings summary has scrolled out of view, which is when this bar pins and takes over the tally the summary card was stating. */
  condensed: boolean;
};

const ORDER: CfiRating[] = ['favorable', 'neutral', 'unfavorable', 'not_applicable'];

/**
 * Preview and Publish, pinned to the top of the report. Always in the flow — hiding it
 * above the fold and revealing it on scroll would shift every finding down by its
 * height the moment it appeared.
 */
export const CfiPublishBar = ({
  canEdit,
  counts,
  reportId,
  onPublish,
  onAddFinding,
  condensed,
}: Props) => {
  // The contract page scrolls as a whole document, so the only thing left at the top of
  // the viewport is the fixed AppBar and sticking at `top: 0` would park this behind it.
  // The AppBar publishes its own measured height; it is not a constant 64.
  const appBarHeight = useAppBarHeight();
  const [isBusy, setIsBusy] = useState(false);

  if (!canEdit) return null;

  const total = ORDER.reduce((sum, rating) => sum + (counts[rating] ?? 0), 0);

  const handlePublish = async () => {
    setIsBusy(true);
    try {
      await onPublish();
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: `${appBarHeight}px`,
        // Above the findings and the contract header band, below the AppBar's own layer.
        zIndex: 2,
        mb: 2,
        py: 1,
        // Chrome only while pinned. At rest the bar is transparent and sits flush under the
        // summary card, so the two read as one block.
        ...(condensed
          ? {
              backgroundColor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              // Bottom corners only — the top edge is flush against the AppBar, so rounding it
              // would open two gaps onto the findings sliding past behind.
              borderBottomLeftRadius: `${SURFACE_RADIUS}px`,
              borderBottomRightRadius: `${SURFACE_RADIUS}px`,
              boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.16)',
            }
          : null),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
      >
        <Fade in={condensed} unmountOnExit>
          <Stack direction="row" alignItems="baseline" spacing={1.5} useFlexGap flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {total} {total === 1 ? 'finding' : 'findings'}
            </Typography>
            {ORDER.map((rating) => (
              <Typography
                key={rating}
                variant="body2"
                color={
                  ratingColors[rating] === 'default'
                    ? 'text.secondary'
                    : `${ratingColors[rating]}.main`
                }
              >
                {counts[rating] ?? 0} {ratingLabels[rating].toLowerCase()}
              </Typography>
            ))}
          </Stack>
        </Fade>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddFinding} sx={BUTTON_SX}>
            Add custom finding
          </Button>
          <CfiDownloadButton
            reportId={reportId}
            preview
            variant="outlined"
            label="Preview report"
          />
          <Button variant="contained" onClick={handlePublish} disabled={isBusy} sx={BUTTON_SX}>
            Publish report
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
